import {concat, emptyArray} from '@packages/utilities/fp/utils';
import {
  isUserComponentInst,
  getComponentInstRootElement,
} from '@packages/utilities/platform';
import {
  isStringElement,
  isTypographic,
  isRichText,
} from '@packages/utilities/elementTypeUtils';
import {singleMemo, weakMemo} from '@packages/utilities/memo';
import {
  Component,
  Expressions,
  matchExpression,
  Types,
} from '@packages/systems/wfdl';
import {getName} from '@packages/systems/wfdl-Qualified';
import {EElement, type ExpressionElement} from '@packages/systems/wfdl-ast';
import type {TranslatedNodeData} from '@packages/systems/localization-types';
import {getTranslatableAltOverrides} from './getTranslatableAltOverrides';
import {getTranslatableAttributesForElement} from './getTranslatableAttributesForElement';
import {collectTextFromDescendants} from './collectTextFromDescendants';
import {
  hasInlineChildren,
  collectTextFromDescendantsWithXmlWrapping,
} from './collectTextFromDescendantsWithXmlWrapping';
import {getTranslatableComponentPropertiesDefaults} from './getTranslatableComponentPropertiesDefaults';
import {
  isDefaultElementText,
  isDefaultRichTextContent,
} from './isDefaultElementText';

function withComponentId(
  items: TranslatedNodeData[],
  componentId: string | undefined
): TranslatedNodeData[] {
  if (!componentId) {
    return items;
  }
  return items.map((item) => ({...item, componentId}));
}

/**
 * NOTE: `collectTranslatableDataFromDescendants` and `extractTranslatableDataFromExpression`
 * are mutually recursive functions and MUST remain in the same file to avoid circular dependencies.
 * Do not split them into separate files.
 *
 * - `extractTranslatableDataFromExpression` calls `collectTranslatableDataFromDescendants` to walk children
 * - `collectTranslatableDataFromDescendants` calls `extractTranslatableDataFromExpression` to process each element
 */

/**
 * Extracts all translatable data from the descendant expression elements.
 * If the element has no children only extracts its translatable attributes.
 *
 * `componentId` identifies the user component instance this walk is nested
 * inside (the enclosing component's canonical Designer identity), so every
 * item produced can be traced back to its owning component's canvas.
 * Unset when walking page-level content outside any component.
 */
export function collectTranslatableDataFromDescendants(
  elementToWalk: ExpressionElement | null,
  components: Component.ComponentMap,
  componentPropertyId: string,
  componentId?: string
): TranslatedNodeData[] {
  if (!elementToWalk) {
    return emptyArray;
  }

  return Expressions.everything<TranslatedNodeData[]>(concat)(
    extractTranslatableDataFromExpression(components)(elementToWalk)(
      componentPropertyId
    )(componentId)
  )(EElement(elementToWalk));
}

export const extractTranslatableDataFromExpression = weakMemo(
  (components: Component.ComponentMap) =>
    weakMemo((elementToWalk: ExpressionElement) =>
      singleMemo((componentPropertyId: string) =>
        singleMemo((componentId: string | undefined) =>
          matchExpression({
            Element: (element) => {
              // A still-pristine Rich Text has nothing real to translate
              // anywhere in it, including List/ListItem and inline-formatting
              // content that isDefaultElementText deliberately doesn't cover
              // element-by-element (see LOC-2755).
              if (isRichText(element) && isDefaultRichTextContent(element)) {
                return Expressions.stop(emptyArray);
              }

              if (isUserComponentInst(components, element)) {
                const componentInstRootElement =
                  getComponentInstRootElement(components)(element);

                if (!componentInstRootElement) {
                  return Expressions.stop(emptyArray);
                }

                const componentInstRootElementComponent = components.get(
                  element.type
                );

                if (!componentInstRootElementComponent) {
                  return Expressions.stop(emptyArray);
                }

                const instanceComponentId = getName(element.type);

                const componentProperties = Types.getRecordFields(
                  Component.getDataType(componentInstRootElementComponent)
                );
                const isLibraryComponent = Boolean(
                  Component.getOrigin(componentInstRootElementComponent)
                );

                let translatablePropDefaults: TranslatedNodeData[] = [];
                let translatableAttrPropOverrides: TranslatedNodeData[] = [];

                if (componentProperties) {
                  // Collect all of the translatable property default values
                  translatablePropDefaults = withComponentId(
                    getTranslatableComponentPropertiesDefaults(
                      componentProperties,
                      Expressions.getElementId(
                        isLibraryComponent ? element : componentInstRootElement
                      )
                    ),
                    instanceComponentId
                  );

                  // Collect the property overrides bound to image alt attributes. Overrides that are
                  // Elements are not handled here but in the parent function because it's recursive.
                  translatableAttrPropOverrides = withComponentId(
                    getTranslatableAltOverrides(element, componentProperties),
                    instanceComponentId
                  );
                }

                let result = concat(
                  // Walk all children in the component
                  collectTranslatableDataFromDescendants(
                    componentInstRootElement,
                    components,
                    componentPropertyId,
                    instanceComponentId
                  )
                )(
                  concat(translatablePropDefaults)(
                    translatableAttrPropOverrides
                  )
                );

                // The instance's own data record holds its property overrides.
                // Walking them here — rather than letting the generic descent
                // below do it — is what threads this instance's componentId and
                // the overridden propId onto the items. Without that, a
                // whole-page walk (`getTranslatableData(pageRoot, [], map)`, as
                // auto-translate-on-publish and the page exclusion gate both
                // use) emits them untagged and the exclusion gate resolves them
                // to the page's canvas instead of this component's prop.
                const overrideRecord = Expressions.getRecord(
                  Expressions.getElementData(element)
                );

                if (!overrideRecord) {
                  return Expressions.resume(result);
                }

                for (const propId in overrideRecord) {
                  result = concat(result)(
                    Expressions.everything<TranslatedNodeData[]>(concat)(
                      extractTranslatableDataFromExpression(components)(
                        elementToWalk
                      )(propId)(instanceComponentId)
                    )(overrideRecord[propId]!)
                  );
                }

                return Expressions.stop(result);
              }

              // For typographic elements, collect all text as a single whole-text unit.
              // When inline children (Span, Link, etc.) are present, wrap them in XML
              // tags so the translation pipeline can preserve and remap them.
              if (isTypographic(element)) {
                const wholeText = hasInlineChildren(element)
                  ? collectTextFromDescendantsWithXmlWrapping(element)
                  : collectTextFromDescendants(element);
                if (!wholeText) {
                  return Expressions.resume(emptyArray);
                }
                if (isDefaultElementText(element.type, wholeText)) {
                  return Expressions.stop(emptyArray);
                }
                return Expressions.stop([
                  {
                    _id: Expressions.getElementId(element),
                    type: 'whole-text',
                    text: true,
                    v: wholeText,
                    translatableParentId:
                      Expressions.getElementId(elementToWalk),
                    ...(componentPropertyId
                      ? {fieldId: componentPropertyId}
                      : {}),
                    ...(componentId ? {componentId} : {}),
                  },
                ]);
              }

              if (isStringElement(element)) {
                const v = Expressions.getText(
                  Expressions.getElementData(element)
                );

                if (!v) {
                  return Expressions.resume(emptyArray);
                }

                return Expressions.resume([
                  {
                    _id: Expressions.getElementId(element),
                    text: true,
                    v,
                    translatableParentId:
                      Expressions.getElementId(elementToWalk),
                    ...(componentPropertyId
                      ? {fieldId: componentPropertyId}
                      : {}),
                    ...(componentId ? {componentId} : {}),
                  },
                ]);
              }

              return Expressions.resume(
                withComponentId(
                  getTranslatableAttributesForElement(element, elementToWalk),
                  componentId
                )
              );
            },
            _: () => Expressions.resume(emptyArray),
          })
        )
      )
    )
);
